import React, { useState, useContext, useRef, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import Loading from 'components/Loading';
import LeadProvider, { LeadContext } from 'Leads/Details/hooks/context';
import AuthProvider from 'api/context';

import Designer from 'IFrame/Designer';
import Proposal from 'IFrame/Proposal';
import Home from 'IFrame/Home';
import IFrameError from 'IFrame/Error';
import { postToParent } from './postMessage';
import {
  createLatestMessageListener,
  isTrustedParentMessage,
  mergeCommandOptions,
  resolveDesignId,
  RUN_PRODUCTION_AND_CREATE_PROPOSAL,
  runPanelPlacementCommand,
  runProductionAndCreateProposal,
} from './commands';
import {
  buildGradientPollingError,
  claimGradientCompletion,
  getGradientPollingElapsedMs,
  getGradientStateKind,
  GRADIENT_POLL_TIMEOUT_MS,
  isGradientPollingSessionActive,
  isGradientPollingTimedOut,
} from './gradient-polling';

import { inchToCm } from 'Leads/Details/DesignTool/utils';
import { normalizeConfig } from './lib';
import { getIframeCompanyName } from './company';

import AuthManager, { client } from 'api/auth';

import { retry } from 'Leads/Details/DesignTool/utils';

import {
  assert,
  string,
  object,
  number,
  nonempty,
  integer,
  optional,
  boolean,
  max,
  enums,
  define,
  size,
  min,
  defaulted,
  array,
  nullable,
  union,
  pattern,
} from 'superstruct';

const coordinatesRegexExp =
  /^((-?|\+?)?\d+(\.\d+)?),\s*((-?|\+?)?\d+(\.\d+)?)$/gi;
const coordinates = () =>
  define('coordinates', (value) => coordinatesRegexExp.test(value));
const positiveNumber = () =>
  define(
    'number > 0',
    (value) => typeof value === 'number' && Number.isFinite(value) && value > 0
  );

const IFrameCoverageAPIScheme = object({
  coordinates: pattern(
    nonempty(string()),
    /^((-?|\+?)?\d+(\.\d+)?),\s*((-?|\+?)?\d+(\.\d+)?)$/
  ),
});

const IFrameNewLeadContactsScheme = object({
  first_name: optional(string()),
  last_name: optional(string()),
  email: optional(string()),
  phone: optional(string()),
});

const IFrameNewLeadPropertyProfile = object({
  roof_type: optional(string()),
  roof_age: optional(number()),
});

const IFrameNewLeadUtilityScheme = object({
  provider: optional(string()),
  rate: optional(string()),
  fixed_charge_units: optional(string()), // $/month
  usage: number(),
  peak_usage: optional(number()),
  fixed_charge: optional(number()),
  rate_amount: optional(number()),
});

const IFrameNewLeadParamsScheme = object({
  contacts: optional(IFrameNewLeadContactsScheme),
  coordinates: pattern(
    nonempty(string()),
    /^((-?|\+?)?\d+(\.\d+)?),\s*((-?|\+?)?\d+(\.\d+)?)$/
  ),
  electricity_usage: positiveNumber(),
  utility: optional(IFrameNewLeadUtilityScheme),
  monthly_bill: optional(number()),
  property_profile: optional(IFrameNewLeadPropertyProfile),
  type: optional(enums(['AI', 'Manual'])),
  rgb: optional(string()),
  dsm: optional(string()),
  resolution: optional(number()),
  includeHourlyProduction: optional(boolean()),
});

const IFrameRerunParamsScheme = object({
  uid: nonempty(string()),
  design_id: optional(nullable(string())),
  canvas_id: optional(nullable(string())),
});

const DESIGNER_LOAD_TIMEOUT_MS = 120000;
const DESIGNER_METHOD_TIMEOUT_MS = 30000;

const IFrameConfigScheme = object({
  design_id: optional(nullable(string())),
  canvas_id: optional(nullable(string())),
  setback: optional(union([integer(), array(number())])),
  buffer: optional(number()),
  panelOrientation: optional(number()),
  panelLayout: optional(number()),
  panelTilt: optional(number()),
  rowSpacing: optional(number()),
  maxDcAcRatio: optional(number()),
  autoSave: optional(boolean()),
  includeHourlyProduction: optional(boolean()),
  targetProduction: optional(number()),
  wiring: optional(
    object({
      ohm_per_m_string: optional(number()),
      loop_factor: optional(number()),
    })
  ),
  powerOptimizer: optional(object()),
  companyPowerOptimizerID: optional(nullable(string())),
  azimuthRestriction: optional(array(number())),
  companyPanelID: optional(nullable(string())),
  companyInverterID: optional(nullable(string())),
  companyAdditionalInverterIDs: optional(
    array(
      union([
        nullable(string()), // Legacy: simple ID string
        object({
          // Enhanced: ID with quantity
          id: string(),
          quantity: defaulted(integer(), 1),
          autoQuantity: optional(boolean()),
        }),
      ])
    )
  ),
  inverterQuantity: optional(integer()),
  primaryInverterAutoQuantity: optional(boolean()),
  losses: optional(
    object({
      soiling: size(number(), 0.0, 99.99),
      // shading: size(number(), 0.01, 99.99),
      snow: size(number(), 0.01, 99.99),
      mismatch: size(number(), 0.0, 99.99),
      wiring: size(number(), 0.01, 99.99),
      connections: size(number(), 0.0, 99.99),
      lid: size(number(), 0.0, 99.99),
      nameplate_rating: size(number(), 0.01, 99.99),
      age: size(number(), 0.0, 99.99),
      availability: size(number(), 0.0, 99.99),
    })
  ),
  panelType: optional(
    object({
      manufacturer: object({ name: nonempty(string()) }),
      length: number(),
      width: number(),
      degradation: size(number(), 0.01, 99.99),
      efficiency: size(number(), 0.01, 99.99),
      has_micro: boolean(),
      model: nonempty(string()),
      power: integer(),
    })
  ),
  panelDefault: optional(string()),
  inverter: optional(
    object({
      capacity: integer(),
      efficiency: size(number(), 0.01, 99.99),
      model: nonempty(string()),
      type: enums(['Micro', 'Central', 'M', 'C', 'P']),
      quantity: defaulted(integer(), 1),
      mppt_count_per_inverter: optional(integer()),
      strings_per_mppt_max: optional(integer()),
    })
  ),
  additionalInverters: optional(
    array(
      union([
        object({
          companyInverterID: string(),
          quantity: defaulted(integer(), 1),
          autoQuantity: optional(boolean()),
        }),
        object({
          capacity: integer(),
          efficiency: size(number(), 0.01, 99.99),
          model: nonempty(string()),
          type: enums(['Micro', 'Central', 'M', 'C', 'P']),
          quantity: defaulted(integer(), 1),
          autoQuantity: optional(boolean()),
          mppt_count_per_inverter: optional(integer()),
          strings_per_mppt_max: optional(integer()),
        }),
      ])
    )
  ),
  defaultBtnView: optional(
    object({
      ShadingSimulator: boolean(),
      Ground: boolean(),
      Trees: boolean(),
      FireSetbacks: boolean(),
      Panels: boolean(),
      House: boolean(),
      ShadingGradients: boolean(),
      DSM: boolean(),
      HouseDSM: boolean(),
      Annotation: optional(boolean()),
    })
  ),
});

const IFrame = () => {
  const debugPrefix = '[IFRAME-DESIGN-DEBUG]';
  const debugLog = (...args) => console.log(debugPrefix, ...args);
  const designer = useRef();
  const proposal = useRef();
  const designerLoadedRef = useRef(false);
  const chainStateRef = useRef({
    shadingHandled: false,
    completionSource: null,
    telemetry: null,
  });
  const handleMessageRef = useRef();
  const gradientPollingRef = useRef(null);

  const [errors, setErrors] = useState();
  const [auth] = useState(AuthManager);

  const [checkGradientsInterval, setCheckGradientsInterval] = useState();

  const location = useLocation();
  const { uid } = useParams();

  const searchParams = new URLSearchParams(location.search);
  const pid = searchParams.get('id');
  const chain = searchParams.get('chain');
  const apiKey = searchParams.get('access-key');
  const designFrame = searchParams.get('design');
  const iframeDesignId = resolveDesignId(null, location.search);
  const debugParam = searchParams.get('debug');
  const getDesignId = (source = null) =>
    resolveDesignId(source, location.search);
  const debugParamEnabled = debugParam === 'true' || debugParam === '1';
  const debugTelemetryEnabled = (() => {
    try {
      const sessionFlag =
        typeof window !== 'undefined' &&
        window.sessionStorage?.getItem('iframe.debugTelemetry') === '1';
      return debugParamEnabled || Boolean(sessionFlag);
    } catch (err) {
      return debugParamEnabled;
    }
  })();

  const telemetryEnabledRef = useRef(debugTelemetryEnabled);
  useEffect(() => {
    telemetryEnabledRef.current = debugTelemetryEnabled;
  }, [debugTelemetryEnabled]);

  useEffect(() => {
    debugLog('IFrame component mounted');
    if (!debugParamEnabled) return;
    try {
      window.sessionStorage?.setItem('iframe.debugTelemetry', '1');
    } catch (err) {}
  }, [debugParamEnabled]);

  useEffect(() => {
    if (!debugTelemetryEnabled || chain !== 'true') return;
    if (chainStateRef.current.telemetry) return;
    const stored = readDebugChain();
    if (stored?.commandId && stored?.chainId) {
      chainStateRef.current.telemetry = stored;
      emitTelemetry({
        event: 'chain_resume',
        cmd: 'generate',
        commandId: stored.commandId,
        chainId: stored.chainId,
        ts: Date.now(),
      });
    }
  }, [chain, debugTelemetryEnabled]);

  const commandTelemetryRef = useRef({
    starts: new Map(),
    pendingByCallback: new Map(),
  });

  const debugChainStorageKey = 'iframe.debugTelemetry.chain';
  const debugSpanStorageKey = (chainId, step) =>
    `iframe.debugTelemetry.span.${chainId}.${step}`;

  const readDebugChain = () => {
    if (!telemetryEnabledRef.current) return null;
    try {
      const raw = window.sessionStorage?.getItem(debugChainStorageKey);
      return raw ? JSON.parse(raw) : null;
    } catch (err) {
      return null;
    }
  };

  const writeDebugChain = (data) => {
    if (!telemetryEnabledRef.current) return;
    try {
      if (!data) {
        window.sessionStorage?.removeItem(debugChainStorageKey);
      } else {
        window.sessionStorage?.setItem(
          debugChainStorageKey,
          JSON.stringify(data)
        );
      }
    } catch (err) {}
  };

  const makeTelemetryId = (prefix) =>
    `${prefix}_${Date.now().toString(36)}_${Math.random()
      .toString(36)
      .slice(2, 8)}`;

  const emitTelemetry = (payload) => {
    if (!telemetryEnabledRef.current || !window.parent) return;
    postToParent('Debug.Timing', payload);
  };

  const startCommandTelemetry = (cmd, meta = {}) => {
    if (!telemetryEnabledRef.current || !cmd) return null;
    const commandId = makeTelemetryId('cmd');
    const chainId = cmd === 'generate' ? makeTelemetryId('chain') : commandId;
    const ts = Date.now();
    commandTelemetryRef.current.starts.set(commandId, {
      cmd,
      chainId,
      ts,
    });
    emitTelemetry({ event: 'command_start', cmd, commandId, chainId, ts, meta });
    if (cmd === 'generate') {
      const chainMeta = { commandId, chainId, startedAt: ts };
      chainStateRef.current.telemetry = chainMeta;
      writeDebugChain(chainMeta);
      const spanId = makeTelemetryId('span');
      const spanKey = debugSpanStorageKey(chainId, 'LeadCreation');
      try {
        window.sessionStorage?.setItem(
          spanKey,
          JSON.stringify({ spanId, ts })
        );
      } catch (err) {}
      emitTelemetry({
        event: 'span_start',
        cmd,
        commandId,
        chainId,
        spanId,
        step: 'LeadCreation',
        ts,
      });
    }
    return { commandId, chainId, ts };
  };

  const endCommandTelemetry = (commandId, status = 'ok', meta = {}) => {
    if (!telemetryEnabledRef.current || !commandId) return;
    const record = commandTelemetryRef.current.starts.get(commandId);
    if (!record) return;
    const ts = Date.now();
    const elapsedMs = Number.isFinite(record.ts) ? ts - record.ts : undefined;
    emitTelemetry({
      event: 'command_end',
      cmd: record.cmd,
      commandId,
      chainId: record.chainId,
      ts,
      elapsedMs,
      status,
      meta,
    });
    commandTelemetryRef.current.starts.delete(commandId);
  };

  const registerCommandCompletion = (commandId, callbackType) => {
    if (!telemetryEnabledRef.current || !commandId || !callbackType) return;
    const pending =
      commandTelemetryRef.current.pendingByCallback.get(callbackType) || [];
    commandTelemetryRef.current.pendingByCallback.set(callbackType, [
      ...pending,
      commandId,
    ]);
  };

  const removeCommandCompletion = (commandId, callbackType) => {
    if (!commandId || !callbackType) return;
    const pending =
      commandTelemetryRef.current.pendingByCallback.get(callbackType) || [];
    const remaining = pending.filter((id) => id !== commandId);
    if (remaining.length) {
      commandTelemetryRef.current.pendingByCallback.set(
        callbackType,
        remaining
      );
    } else {
      commandTelemetryRef.current.pendingByCallback.delete(callbackType);
    }
  };

  const completeCommandForCallback = (callbackType, status = 'ok', meta = {}) => {
    if (!telemetryEnabledRef.current || !callbackType) return;
    const pending =
      commandTelemetryRef.current.pendingByCallback.get(callbackType) || [];
    const commandId = pending[0];
    if (!commandId) return;
    endCommandTelemetry(commandId, status, meta);
    removeCommandCompletion(commandId, callbackType);
  };

  const getActiveChainTelemetry = () => {
    return chainStateRef.current.telemetry || readDebugChain();
  };

  const startChainSpan = (step, meta = {}) => {
    if (!telemetryEnabledRef.current || !step) return null;
    const chain = getActiveChainTelemetry();
    if (!chain?.commandId || !chain?.chainId) return null;
    const spanId = makeTelemetryId('span');
    const ts = Date.now();
    emitTelemetry({
      event: 'span_start',
      cmd: 'generate',
      commandId: chain.commandId,
      chainId: chain.chainId,
      spanId,
      step,
      ts,
      meta,
    });
    try {
      window.sessionStorage?.setItem(
        debugSpanStorageKey(chain.chainId, step),
        JSON.stringify({ spanId, ts })
      );
    } catch (err) {}
    return spanId;
  };

  const endChainSpan = (step, status = 'ok', meta = {}) => {
    if (!telemetryEnabledRef.current || !step) return;
    const chain = getActiveChainTelemetry();
    if (!chain?.commandId || !chain?.chainId) return;
    let spanId;
    let startTs;
    try {
      const raw = window.sessionStorage?.getItem(
        debugSpanStorageKey(chain.chainId, step)
      );
      if (raw) {
        const parsed = JSON.parse(raw);
        spanId = parsed?.spanId;
        startTs = parsed?.ts;
      }
    } catch (err) {}
    const ts = Date.now();
    const elapsedMs = Number.isFinite(startTs) ? ts - startTs : undefined;
    emitTelemetry({
      event: 'span_end',
      cmd: 'generate',
      commandId: chain.commandId,
      chainId: chain.chainId,
      spanId,
      step,
      ts,
      elapsedMs,
      status,
      meta,
    });
    try {
      window.sessionStorage?.removeItem(
        debugSpanStorageKey(chain.chainId, step)
      );
    } catch (err) {}
  };

  const endGenerateCommandTelemetry = (status = 'ok', meta = {}) => {
    if (!telemetryEnabledRef.current) return;
    const chain = getActiveChainTelemetry();
    if (!chain?.commandId || !chain?.chainId) return;
    const ts = Date.now();
    const elapsedMs = Number.isFinite(chain.startedAt)
      ? ts - chain.startedAt
      : undefined;
    emitTelemetry({
      event: 'command_end',
      cmd: 'generate',
      commandId: chain.commandId,
      chainId: chain.chainId,
      ts,
      elapsedMs,
      status,
      meta,
    });
    writeDebugChain(null);
    const steps = [
      'LeadCreation',
      'LoadLead',
      'Shading',
      'PanelPlacement',
      'Production',
      'Proposal',
    ];
    steps.forEach((step) => {
      try {
        window.sessionStorage?.removeItem(
          debugSpanStorageKey(chain.chainId, step)
        );
      } catch (err) {}
    });
    commandTelemetryRef.current.starts.delete(chain.commandId);
    chainStateRef.current.telemetry = null;
  };

  const getDesignIdFromPayload = (payload = {}) => getDesignId(payload);

  const removeShadingGradients = async (uid, params = {}) => {
    if (uid === undefined) return null;
    const api_key = auth.getApiKey();
    const headers = {
      Authorization: `Api-Key ${api_key}`,
    };

    const request = { uid };
    const designId = getDesignIdFromPayload(params);
    if (designId) request.design_id = designId;

    try {
      const response = await client.post(
        'designer/gradients/remove/',
        request,
        {
          headers,
        }
      );
      console.log(`Old shading gradients are removed: ${response.status}`);
      return response;
    } catch (error) {
      console.log(`Error in removing shading gradients: ${error}`);
      throw new Error('Failed to clear previous shading gradients');
    }
  };

  const validateLeadExists = async (uid) => {
    if (!uid) {
      throw new Error('Lead uid is required');
    }
    const api_key = auth.getApiKey();
    const headers = {
      Authorization: `Api-Key ${api_key}`,
    };
    try {
      await client.get(`crm/leads/${uid}/`, { headers });
    } catch (err) {
      throw new Error(`Lead with uid ${uid} not found`);
    }
  };

  const waitForDesignerTool = async (
    requiredMethods = [],
    timeoutMs = DESIGNER_METHOD_TIMEOUT_MS,
    pollIntervalMs = 100
  ) => {
    if (typeof designer.current?.waitForMethods === 'function') {
      return designer.current.waitForMethods(
        requiredMethods,
        timeoutMs,
        pollIntervalMs
      );
    }
    debugLog('waitForDesignerTool:wrapper-missing-waitForMethods', {
      hasWrapper: Boolean(designer.current),
      wrapperMethods: designer.current ? Object.keys(designer.current) : [],
    });
    throw new Error('Timed out waiting for designer tool methods');
  };

  const waitForDesignerLoaded = async (
    timeoutMs = DESIGNER_LOAD_TIMEOUT_MS,
    pollIntervalMs = 1000
  ) => {
    const startedAt = Date.now();
    while (Date.now() - startedAt < timeoutMs) {
      if (designerLoadedRef.current || designer.current?.checkIsLoaded?.()) {
        debugLog('checkIsLoaded:success', {
          loadedCallbackReceived: designerLoadedRef.current,
          checkIsLoaded: designer.current?.checkIsLoaded?.(),
        });
        return true;
      }
      debugLog('checkIsLoaded:waiting', {
        loadedCallbackReceived: designerLoadedRef.current,
      });
      await new Promise((resolve) => setTimeout(resolve, pollIntervalMs));
    }
    return false;
  };

  const isCurrentDesignerLead = (leadUid, options = {}) => {
    if (!leadUid) return false;
    if (typeof designer.current?.isCurrentLead === 'function') {
      return designer.current.isCurrentLead(leadUid, options);
    }
    const currentUid = designer.current?.uid || uid;
    return (
      String(leadUid) === String(currentUid || '') &&
      String(getDesignId(options) || '') === String(iframeDesignId || '')
    );
  };

  const stopGradientPolling = () => {
    gradientPollingRef.current = null;
    setCheckGradientsInterval((interval) => {
      clearInterval(interval);
      return null;
    });
  };

  const isCallbackDrivenShadingChainActive = () =>
    chain === 'true' && chainStateRef.current.shadingHandled;

  const getGradientPollingSession = (pollUid, commandId = null) => {
    const current = gradientPollingRef.current;
    if (
      current &&
      String(current.uid || '') === String(pollUid || '') &&
      String(current.commandId || '') === String(commandId || '')
    ) {
      return current;
    }

    const session = {
      uid: pollUid,
      commandId,
      startedAt: Date.now(),
      timeoutMs: GRADIENT_POLL_TIMEOUT_MS,
      errorEmitted: false,
    };
    gradientPollingRef.current = session;
    return session;
  };

  const failGradientPolling = ({
    state = null,
    commandId = null,
    message = null,
  } = {}) => {
    const session = gradientPollingRef.current || {};
    if (session.errorEmitted) return;

    const elapsedMs = getGradientPollingElapsedMs(session);
    const resolvedMessage =
      message ||
      buildGradientPollingError({
        state,
        elapsedMs,
        timeoutMs: session.timeoutMs || GRADIENT_POLL_TIMEOUT_MS,
      });
    gradientPollingRef.current = { ...session, errorEmitted: true };
    stopGradientPolling();
    endChainSpan('Shading', 'error', { message: resolvedMessage });
    endGenerateCommandTelemetry('error', {
      step: 'Shading',
      message: resolvedMessage,
    });
    endCommandTelemetry(commandId || session.commandId, 'error', {
      message: resolvedMessage,
    });
    if (window.parent) {
      postToParent('Error', {
        code: '52',
        message: resolvedMessage,
      });
    }
  };

  const checkShadingGradients = (
    uid,
    params = null,
    loadAfter = false,
    commandId = null
  ) => {
    if (uid === undefined) return;
    if (isCallbackDrivenShadingChainActive()) {
      stopGradientPolling();
      return;
    }
    const pollSession = getGradientPollingSession(uid, commandId);
    if (
      isGradientPollingTimedOut(
        pollSession,
        Date.now(),
        pollSession.timeoutMs
      )
    ) {
      failGradientPolling({ commandId });
      return;
    }

    const api_key = auth.getApiKey();
    const headers = {
      Authorization: `Api-Key ${api_key}`,
    };
    const request = { uid };
    const designId = getDesignIdFromPayload(params);
    if (designId) request.design_id = designId;

    client
      .post(
        'designer/gradients/check/',
        request,
        {
          headers,
        }
      )
      .then(async (res) => {
        if (
          !isGradientPollingSessionActive(
            gradientPollingRef.current,
            pollSession
          )
        ) {
          return;
        }
        if (isCallbackDrivenShadingChainActive()) {
          stopGradientPolling();
          return;
        }
        const state = res.data;
        const stateKind = getGradientStateKind(state);

        if (stateKind === 'ok') {
          if (
            chain === 'true' &&
            !claimGradientCompletion(chainStateRef.current, 'poll')
          ) {
            stopGradientPolling();
            return;
          }
          stopGradientPolling();
          try {
            startChainSpan('Production');
            await runProductionFromChain(params);
            endChainSpan('Production');
          } catch (err) {
            console.log(err);
            endChainSpan('Production', 'error', { message: String(err) });
            endGenerateCommandTelemetry('error', {
              step: 'Production',
              message:
                err?.message || 'Solar production calculation failed',
            });
            endCommandTelemetry(commandId, 'error', {
              message: err?.message || 'Solar production calculation failed',
            });
            if (window.parent)
              return postToParent('Error', {
                code: '60',
                message:
                  err?.message || 'Solar production calculation failed',
              });
            return;
          }

          try {
            startChainSpan('Proposal');
            await designer.current.handleProposal(params, loadAfter);
            endChainSpan('Proposal');
            endGenerateCommandTelemetry('ok', { step: 'Proposal' });
            endCommandTelemetry(commandId);
          } catch (err) {
            console.log(err);
            endChainSpan('Proposal', 'error', { message: String(err) });
            endGenerateCommandTelemetry('error', {
              step: 'Proposal',
              message: 'Error in creating proposal',
            });
            endCommandTelemetry(commandId, 'error', {
              message: 'Error in creating proposal',
            });
            if (window.parent)
              return postToParent('Error', {
                code: '53',
                message: 'Error in creating proposal',
              });
          }
        } else if (stateKind === 'failure') {
          failGradientPolling({ state, commandId });
        } else {
          console.log(`shading gradient state: ${state}`);
          if (
            isGradientPollingTimedOut(
              pollSession,
              Date.now(),
              pollSession.timeoutMs
            )
          ) {
            failGradientPolling({ state, commandId });
          }
        }
      })
      .catch((err) => {
        if (
          !isGradientPollingSessionActive(
            gradientPollingRef.current,
            pollSession
          )
        ) {
          return;
        }
        console.log(`Shading gradients file state is unknown: ${err}`);
        if (
          isGradientPollingTimedOut(
            pollSession,
            Date.now(),
            pollSession.timeoutMs
          )
        ) {
          failGradientPolling({ commandId });
        }
      });
  };

  const startGradientPolling = (
    pollUid,
    params = null,
    loadAfter = false,
    commandId = null
  ) => {
    gradientPollingRef.current = {
      uid: pollUid,
      commandId,
      startedAt: Date.now(),
      timeoutMs: GRADIENT_POLL_TIMEOUT_MS,
      errorEmitted: false,
    };
    const interval = setInterval(
      () => checkShadingGradients(pollUid, params, loadAfter, commandId),
      4000
    );
    setCheckGradientsInterval((current) => {
      clearInterval(current);
      return interval;
    });
  };

  const renameConfigKeys = (config) => {
    const renamedConfig = {};
    Object.keys(config).map((key) => {
      try {
        if (key === 'panelType' || key === 'panel') {
          const panelType = {};
          Object.keys(config[key]).map((panelKey) => {
            // if (panelKey === 'dimensions') {
            //   panelType.length = config[key].dimensions.length;
            //   panelType.width = config[key].dimensions.width;
            // } else if (panelKey === 'manufacturer') {
            //   const manufacturer = { name: config[key].manufacturer };
            //   panelType.manufacturer = manufacturer;
            // } else {
            //   panelType[panelKey] = config[key][panelKey];
            // }
            panelType[panelKey] = config[key][panelKey];
          });
          renamedConfig.panelType = panelType;
        } else {
          renamedConfig[key] = config[key];
        }
      } catch {
        designer.current.displayValidationErrors(
          `A Error exists in Config - Key: ${key}, Value: ${config[key]}`
        );
      }
    });
    return renamedConfig;
  };

  const getDesignerProductionHandlers = () => {
    const root = designer.current;
    const nested = root?.designer?.current;
    return {
      handleIframeSolarProduction:
        nested?.handleIframeSolarProduction ||
        root?.handleIframeSolarProduction,
      applyProductionSettings:
        nested?.applyProductionSettings || root?.applyProductionSettings,
      handleSolarProduction:
        nested?.handleSolarProduction || root?.handleSolarProduction,
    };
  };

  const waitForDesignerProductionHandlers = async (
    timeoutMs = 5000,
    pollIntervalMs = 100
  ) => {
    const startedAt = Date.now();
    while (Date.now() - startedAt < timeoutMs) {
      const handlers = getDesignerProductionHandlers();
      if (
        typeof handlers?.handleIframeSolarProduction === 'function' ||
        typeof handlers?.handleSolarProduction === 'function'
      ) {
        return handlers;
      }
      await new Promise((resolve) => setTimeout(resolve, pollIntervalMs));
    }
    return getDesignerProductionHandlers();
  };

  const runProductionFromChain = async (settingsOverride = null) => {
    const {
      applyProductionSettings,
      handleIframeSolarProduction,
      handleSolarProduction,
    } = await waitForDesignerProductionHandlers();
    if (!handleIframeSolarProduction && !handleSolarProduction) {
      throw new Error('Designer solar production handler is not ready');
    }

    const resolvedSettings =
      settingsOverride && typeof settingsOverride === 'object'
        ? settingsOverride
        : configStateRef.current || configState || {};

    const productionSettings = {
      ...resolvedSettings,
      onlySelectedPanels: false,
    };

    if (handleIframeSolarProduction) {
      await handleIframeSolarProduction(productionSettings, {
        onlySelectedPanels: false,
        persistSelectionState: true,
        throwOnError: true,
      });
      return;
    }

    if (applyProductionSettings) {
      await applyProductionSettings(productionSettings);
    }

    await handleSolarProduction({
      onlySelectedPanels: false,
      persistSelectionState: true,
      throwOnError: true,
    });
  };

  const countPanels = (panels = []) => {
    if (!Array.isArray(panels)) {
      const directCount = Number(
        panels?.panel_count ?? panels?.panelCount ?? panels?.total_panels ?? 0
      );
      return Number.isFinite(directCount) && directCount > 0 ? directCount : 0;
    }
    return panels.reduce((sum, roofPanels) => {
      const directCount = Number(
        roofPanels?.panel_count ??
          roofPanels?.panelCount ??
          roofPanels?.total_panels ??
          0
      );
      if (Number.isFinite(directCount) && directCount > 0) {
        return sum + directCount;
      }
      const horizontal = roofPanels?.horizontal?.points?.length || 0;
      const vertical = roofPanels?.vertical?.points?.length || 0;
      return sum + horizontal + vertical;
    }, 0);
  };

  const countPanelSelection = (selection = null, binaryValues = false) => {
    if (!selection || typeof selection !== 'object') {
      return 0;
    }

    return Object.values(selection).reduce((total, roofPanels) => {
      if (Array.isArray(roofPanels)) {
        return (
          total +
          roofPanels.reduce(
            (sum, value) =>
              sum + (binaryValues ? (Number(value) === 1 ? 1 : 0) : 1),
            0
          )
        );
      }

      if (roofPanels && typeof roofPanels === 'object') {
        return total + Object.keys(roofPanels).length;
      }

      return total;
    }, 0);
  };

  const countSelectedCsv = (value = '') => {
    if (!value || typeof value !== 'string') {
      return 0;
    }
    return value
      .split(',')
      .reduce((sum, entry) => sum + (Number(entry) === 1 ? 1 : 0), 0);
  };

  const countDesignPanels = (design = []) => {
    if (!Array.isArray(design)) {
      return 0;
    }

    return design.reduce((sum, designItem = {}) => {
      const selectedCount =
        countSelectedCsv(designItem?.vertical_selected) +
        countSelectedCsv(designItem?.horizontal_selected);
      if (selectedCount > 0) {
        return sum + selectedCount;
      }

      const panelObjectCount = countPanels(designItem?.panels);
      if (panelObjectCount > 0) {
        return sum + panelObjectCount;
      }

      const total =
        Number(
          designItem?.total_panels ??
            designItem?.totalPanels ??
            designItem?.panel_count ??
            designItem?.panelCount ??
            0
        ) ||
        Number(designItem?.vertical || 0) +
          Number(designItem?.horizontal || 0);
      return sum + (Number.isFinite(total) ? total : 0);
    }, 0);
  };

  const getDesignerPanelCount = () => {
    const root = designer.current;
    const nested = root?.designer?.current;
    const state = nested?.state || root?.state || {};
    const counts = [
      countPanels(state?.panels),
      Number(state?.selectedPanelCount || 0),
      Number(state?.totalPanels || 0),
      countPanelSelection(state?.selectedPanels, true),
      countPanelSelection(state?.selectedObjects?.panels),
      countDesignPanels(state?.design),
      countDesignPanels(state?.design_data?.roofs),
      Number(state?.panel_count || 0),
      Number(state?.panelCount || 0),
      Number(state?.total_panels || 0),
      Number(state?.design_data?.summary?.total_panels || 0),
      Number(state?.design_data?.summary?.totalPanels || 0),
    ].filter((count) => Number.isFinite(count) && count > 0);
    return counts.length > 0 ? Math.max(...counts) : 0;
  };

  const waitForPanelsToLoad = async (
    timeoutMs = 30000,
    pollIntervalMs = 250
  ) => {
    const startedAt = Date.now();
    while (Date.now() - startedAt < timeoutMs) {
      if (getDesignerPanelCount() > 0) {
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, pollIntervalMs));
    }
    throw new Error('Timed out waiting for panel data');
  };

  const waitForDesignerReady = async (
    timeoutMs = 15000,
    pollIntervalMs = 100
  ) => {
    const startedAt = Date.now();
    while (Date.now() - startedAt < timeoutMs) {
      if (
        designer.current &&
        typeof designer.current.checkCoverage === 'function'
      ) {
        return true;
      }
      await new Promise((resolve) => setTimeout(resolve, pollIntervalMs));
    }
    return false;
  };

  const callback = async (callbackType, data) => {
    debugLog('callback:received', { callbackType, data });
    if (callbackType === 'Loaded') {
      designerLoadedRef.current = true;
      debugLog('callback:Loaded:set-ready');
    }
    if (telemetryEnabledRef.current) {
      const status = callbackType === 'Error' ? 'error' : 'ok';
      completeCommandForCallback(callbackType, status, { callbackType });
      if (callbackType === 'Error') {
        endGenerateCommandTelemetry('error', {
          code: data?.code,
          message: data?.message,
        });
      }
    }
    if (chain === 'true' || callbackType === 'NewLead') {
      switch (callbackType) {
        case 'NewLead':
          endChainSpan('LeadCreation', 'ok', { leadId: data });
          startChainSpan('LoadLead', {
            leadId: data,
            design_id: null,
          });
          {
            const nextConfig = { ...(configStateRef.current || {}) };
            delete nextConfig.design_id;
            delete nextConfig.canvas_id;
            configStateRef.current = nextConfig;
            setConfigState(nextConfig);
            try {
              localStorage.setItem('iframe.config', JSON.stringify(nextConfig));
            } catch (err) {}
          }
          chainStateRef.current = {
            shadingHandled: false,
            completionSource: null,
            telemetry: chainStateRef.current.telemetry,
          };
          designer.current.loadNewLead(data, true);
          break;
        case 'Loaded':
          endChainSpan('LoadLead');
          await new Promise((r) => setTimeout(r, 6000));

          const emptySegments =
            designer.current.handleCurrentAccordion('segments');

          await new Promise((r) => setTimeout(r, 2000));

          if (!emptySegments) {
            if (window.parent) {
              endGenerateCommandTelemetry('error', {
                step: 'Loaded',
                message: '0 number of predicted roof segments',
              });
              return postToParent('Error', {
                code: '42',
                message: '0 number of predicted roof segments',
              });
            }
            return;
          }

          designer.current.runEdgeDetectionHandler(true);

          await new Promise((r) => setTimeout(r, 3000));

          // designer.current.handleMeasure();
          designer.current.handleCurrentAccordion('shading');

          await new Promise((r) => setTimeout(r, 500));

          try {
            startChainSpan('Shading');
            const selectedDataset = designer.current.state?.weatherDatasetSelected;
            const fetchedPath = designer.current.state?.weatherDatasetFetchedPath;
            if (!isWeatherDatasetReady(selectedDataset, fetchedPath)) {
              await ensureWeatherDatasetReady(
                designer.current,
                selectedDataset || 'nsrdb_goes_v4_2024'
              );
            }
            const shadingResult = await designer.current.handleShading();
            if (shadingResult === false) {
              throw new Error('Error in shading simulation');
            }
          } catch (err) {
            console.log(`error in handling shading - iframe: ${err}`);
            endChainSpan('Shading', 'error', { message: String(err) });
            endGenerateCommandTelemetry('error', {
              step: 'Shading',
              message: 'Error in shading simulation',
            });
            postToParent('Error', {
              code: '52',
              message: 'Error in shading simulation',
            });
            break;
          }

          startGradientPolling(uid, null, false);
          break;
        // case 'Measure':
        //   designer.current.handleCurrentAccordion('segments');
        //   // designer.current.runEdgeDetectionHandler(true);
        //   designer.current.runOptimizationHandler(false);
        //   break;
        // case 'EdgeLabeling':
        //   designer.current.handleCurrentAccordion('segments');
        //   designer.current.runOptimizationHandler(false);
        //   break;
        // case 'Optimize':
        //   designer.current.handleCurrentAccordion('shading');
        //   designer.current.handleShading();
        //   break;
        case 'Shading':
          if (typeof data === 'string' && data.trim() !== '') {
            break;
          }
          if (!claimGradientCompletion(chainStateRef.current, 'callback')) {
            break;
          }
          stopGradientPolling();
          endChainSpan('Shading');
          designer.current.handleCurrentAccordion('revise');

          await new Promise((r) => setTimeout(r, 2500));

          try {
            startChainSpan('PanelPlacement');
            await designer.current.handlePlacement(data);
            endChainSpan('PanelPlacement');
          } catch (err) {
            endChainSpan('PanelPlacement', 'error', { message: String(err) });
            endGenerateCommandTelemetry('error', {
              step: 'PanelPlacement',
              message: 'Error in panel placement',
            });
            return postToParent('Error', {
              code: '54',
              message: 'Error in panel placement',
            });
          }

          try {
            startChainSpan('Production');
            await runProductionFromChain();
            endChainSpan('Production');
          } catch (err) {
            endChainSpan('Production', 'error', { message: String(err) });
            endGenerateCommandTelemetry('error', {
              step: 'Production',
              message: 'Error in solar production',
            });
            return postToParent('Error', {
              code: '60',
              message: 'Error in solar production',
            });
          }

          // const shadingInterval = setInterval(() => checkShadingGradients(uid, null, false), 4000);
          // setCheckGradientsInterval(shadingInterval);

          try {
            startChainSpan('Proposal');
            await designer.current.handleProposal();
            endChainSpan('Proposal');
            endGenerateCommandTelemetry('ok', { step: 'Proposal' });
          } catch (err) {
            endChainSpan('Proposal', 'error', { message: String(err) });
            endGenerateCommandTelemetry('error', {
              step: 'Proposal',
              message: 'Error in creating proposal',
            });
            return postToParent('Error', {
              code: '53',
              message: 'Error in creating proposal',
            });
          }
          break;
        case 'ShadingCalcError':
          if (
            !claimGradientCompletion(
              chainStateRef.current,
              'callback-error'
            )
          ) {
            break;
          }
          stopGradientPolling();
          endChainSpan('Shading', 'error', { message: 'ShadingCalcError' });
          designer.current.handleCurrentAccordion('revise');
          await new Promise((r) => setTimeout(r, 500));

          try {
            startChainSpan('PanelPlacement');
            await designer.current.handlePlacement(data);
            endChainSpan('PanelPlacement');
          } catch (err) {
            endChainSpan('PanelPlacement', 'error', { message: String(err) });
            endGenerateCommandTelemetry('error', {
              step: 'PanelPlacement',
              message: 'Error in panel placement',
            });
            return postToParent('Error', {
              code: '54',
              message: 'Error in panel placement',
            });
          }

          try {
            startChainSpan('Production');
            await runProductionFromChain();
            endChainSpan('Production');
          } catch (err) {
            endChainSpan('Production', 'error', { message: String(err) });
            endGenerateCommandTelemetry('error', {
              step: 'Production',
              message: 'Error in solar production',
            });
            return postToParent('Error', {
              code: '60',
              message: 'Error in solar production',
            });
          }

          try {
            startChainSpan('Proposal');
            await designer.current.handleProposal();
            endChainSpan('Proposal');
            endGenerateCommandTelemetry('ok', { step: 'Proposal' });
          } catch (err) {
            endChainSpan('Proposal', 'error', { message: String(err) });
            endGenerateCommandTelemetry('error', {
              step: 'Proposal',
              message: 'Error in creating proposal',
            });
            return postToParent('Error', {
              code: '53',
              message: 'Error in creating proposal',
            });
          }
          break;
        case 'PanelPlacement':
          break;
        /*
        case 'PanelPlacement':
          setTimeout(() => designer.current.handleProposal(), 5250);
          // designer.current.handleProposal();
          break;
        */
        case 'NewProposal':
          proposal.current.loadProposal(uid, data, {
            ...configStateRef.current,
          });
          endGenerateCommandTelemetry('ok', { step: 'NewProposal' });
          break;
        default:
          break;
      }
    } else if (callbackType === 'Error') {
      // setErrors(data);
      if (uid) {
        const api_key = auth.getApiKey();
        const headers = {
          Authorization: `Api-Key ${api_key}`,
        };
        client
          .post(
            'iframe/debug/',
            {
              uid,
              data,
            },
            {
              headers,
            }
          )
          .catch((err) => {
            console.log('Error in sending back debug msg:', err);
          });
      }
    }
  };

  const [configState, setConfigState] = useState(() => {
    try {
      const raw = localStorage.getItem('iframe.config');
      if (!raw) return {};
      const cached = normalizeConfig(JSON.parse(raw) || {});
      delete cached.design_id;
      delete cached.canvas_id;
      localStorage.setItem('iframe.config', JSON.stringify(cached));
      return cached;
    } catch {
      return {};
    }
  });
  const configStateRef = useRef(configState);

  useEffect(() => {
    configStateRef.current = configState;
  }, [configState]);

  const postMessage = (cmd, data, version) => {
    postToParent(`${cmd}`, data, '*', version);
  };

  const accordionKeys = new Set([
    'segments',
    'obstacles',
    'trees',
    'setback',
    'design',
    'revise',
    'shading',
    'production',
    'annotations',
  ]);

  const normalizeAccordionKey = (value) => {
    if (typeof value !== 'string') return value;
    const trimmed = value.trim();
    if (!trimmed) return value;
    const camelized = trimmed.replace(/([a-z])([A-Z])/g, '$1_$2');
    const normalized = camelized.toLowerCase().replace(/[\s-]+/g, '_');
    return accordionKeys.has(normalized) ? normalized : trimmed;
  };

  const parseDatasetKeyFromPath = (path) => {
    try {
      if (!path || typeof path !== 'string') return null;
      const base = path.split('/').pop() || '';
      if (!base.endsWith('.csv')) return null;
      return base.slice(0, -'.csv'.length) || null;
    } catch (err) {
      return null;
    }
  };

  const isWeatherDatasetReady = (selected, fetchedPath) => {
    const selectedKey = typeof selected === 'string' ? selected : null;
    const fetchedKey = parseDatasetKeyFromPath(fetchedPath);
    if (!selectedKey) return Boolean(fetchedKey);
    return fetchedKey === selectedKey;
  };

  const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const pollWeatherDatasetStatus = async (
    designerInstance,
    datasetKey,
    taskId
  ) => {
    const endpoint = designerInstance?.context?.newEndpoint?.(
      'api/nsrdb/status/'
    );
    const leadUid = designerInstance?.context?.lead?.uid;
    if (!endpoint || !leadUid) {
      throw new Error('Weather dataset status endpoint unavailable');
    }
    const start = Date.now();
    let attempt = 0;
    const timeoutMs = 120000;

    while (Date.now() - start < timeoutMs) {
      attempt += 1;
      const params = new URLSearchParams();
      if (taskId) {
        params.set('task_id', taskId);
      } else {
        params.set('uid', leadUid);
        params.set('dataset', datasetKey);
      }
      const designId = designerInstance?.context?.designId;
      if (designId) params.set('design_id', designId);
      const query = `?${params.toString()}`;
      // eslint-disable-next-line no-await-in-loop
      const res = await endpoint.list(query);
      if (res?.status === 'ready' && res?.weather_dataset) {
        designerInstance.handleWeatherDatasetFetched(res.weather_dataset);
        return true;
      }
      if (res?.status === 'failed' || res?.success === false) {
        throw new Error(res?.error || 'Failed to fetch weather dataset');
      }
      const delayMs = Math.min(1000 + attempt * 500, 5000);
      // eslint-disable-next-line no-await-in-loop
      await wait(delayMs);
    }

    throw new Error('Timed out waiting for weather dataset');
  };

  const ensureWeatherDatasetReady = async (designerInstance, datasetKey) => {
    const endpoint = designerInstance?.context?.newEndpoint?.(
      'api/nsrdb/fetch/'
    );
    const leadUid = designerInstance?.context?.lead?.uid;
    if (!endpoint || !leadUid) {
      return false;
    }
    const payload = { uid: leadUid, dataset: datasetKey };
    const designId = designerInstance?.context?.designId;
    if (designId) payload.design_id = designId;
    const res = await endpoint.create(payload);
    if (!res || res.success === false) {
      throw new Error(res?.error || 'Failed to fetch weather dataset');
    }
    if (res?.weather_dataset) {
      designerInstance.handleWeatherDatasetFetched(res.weather_dataset);
      return true;
    }
    if (res?.status === 'pending') {
      return pollWeatherDatasetStatus(designerInstance, datasetKey, res.task_id);
    }
    return true;
  };

  const handleUpdateTree = (designerInstance, params) => {
    const treeIndex = params?.index;
    const baseTree =
      Number.isInteger(treeIndex) &&
      Array.isArray(designerInstance.state?.trees)
        ? designerInstance.state.trees[treeIndex]
        : null;
    const treeUpdate =
      params?.tree && typeof params.tree === 'object' ? params.tree : {};
    const nextTree = baseTree
      ? { ...baseTree, ...treeUpdate }
      : { ...treeUpdate };
    const trunkHeightRaw = treeUpdate?.trunkHeight;

    if (trunkHeightRaw !== undefined) {
      const trunkHeightNum = Number(trunkHeightRaw);
      if (Number.isFinite(trunkHeightNum)) {
        nextTree.trunkHeight = trunkHeightNum;
      }
    }

    const heightNum = Number(nextTree?.height);
    if (Number.isFinite(heightNum)) {
      nextTree.height = heightNum;
    }

    if (
      Number.isFinite(nextTree?.trunkHeight) &&
      Number.isFinite(nextTree?.height) &&
      nextTree.trunkHeight > nextTree.height
    ) {
      nextTree.trunkHeight = nextTree.height;
    }

    if (Object.keys(nextTree).length > 0 && Number.isInteger(treeIndex)) {
      designerInstance.handleTreeUpdate(nextTree, treeIndex);
    }
  };

  const handleSetInverterQuantity = (designerInstance, params) => {
    if (params?.quantity && designerInstance.changeInverterQuantity) {
      designerInstance.changeInverterQuantity(params.quantity);
    }

    const autoOverride =
      typeof params?.primaryInverterAutoQuantity === 'boolean'
        ? params.primaryInverterAutoQuantity
        : params?.resetAutoMode === true
        ? true
        : params?.resetAutoMode === false
        ? false
        : null;

    if (autoOverride !== null && designerInstance.setStatex) {
      designerInstance.setStatex(
        {
          primaryInverterAutoQuantity: autoOverride,
          hasAutoCalculatedInverterQuantity: !autoOverride,
        },
        () => {
          if (designerInstance.context?.CanvasAPI?.saveCanvas) {
            designerInstance.context.CanvasAPI.saveCanvas({
              primary_inverter_auto_quantity: autoOverride,
            });
          }
        }
      );
    }
  };

  const handleUIMessage = async (cmd, params, event) => {
    const d = designer?.current?.designer?.current;
    if (!d) return postMessage('Error', { message: 'lead not loaded' });
    switch (cmd) {
      case 'ui.switch_view':
        d.switchView(params.viewMode);
        break;
      case 'ui.get_state':
        postMessage('State', d.getUIState());
        break;
      case 'ui.set_current_accordion':
        d.handleCurrentAccordion(normalizeAccordionKey(params?.accordion));
        break;
      case 'ui.set_pitch_unit':
        if (typeof params?.pitchInDegrees === 'boolean') {
          d.handlePitchInDegreesChange(params.pitchInDegrees);
        } else if (typeof params?.pitch_in_degrees === 'boolean') {
          d.handlePitchInDegreesChange(params.pitch_in_degrees);
        }
        break;
      case 'ui.measure':
        d.handleMeasure();
        break;
      case 'ui.optimize':
        d.runOptimizationHandler(false);
        break;
      case 'ui.edge_detection':
        d.runEdgeDetectionHandler(true);
        break;
      case 'ui.run_ai':
        d.runAIHandler();
        break;
      case 'ui.set_pitch':
        d.handleSegmentPitchAndArea(params.index, params.pitch);
        break;
      case 'ui.set_azimuth':
        d.handleSegmentAzimuth(params.index, params.azimuth);
        break;
      case 'ui.set_eave_height':
        d.handleSegmentEaveHeight(params.index, params.eave_height);
        break;
      case 'ui.set_all_pitch':
        d.handleSegmentPitchAndArea(-1, params.pitch);
        break;
      case 'ui.set_all_eave_height':
        d.handleSegmentEaveHeight(-1, params.eave_height);
        break;
      case 'ui.add_point':
        if (params?.id) {
          d.handleAddPointToPolygonFromMenu(params.id);
        } else if (Number.isInteger(params?.index)) {
          const segmentId = d.state?.segments?.[params.index]?.id;
          if (segmentId) d.handleAddPointToPolygonFromMenu(segmentId);
        }
        break;
      case 'ui.remove_point':
        if (params?.id) {
          d.handleRemovePointFromPolygonFromMenu(params.id);
        } else if (Number.isInteger(params?.index)) {
          const segmentId = d.state?.segments?.[params.index]?.id;
          if (segmentId) d.handleRemovePointFromPolygonFromMenu(segmentId);
        }
        break;
      case 'ui.remove_obj':
        d.handleDeleteShape(params.id);
        break;
      case 'ui.clear_labels':
        d.clearLabelsHandler();
        break;
      case 'ui.label_mode':
        d.handleSelectedLabel(params.label);
        break;
      case 'ui.mode':
        d.handleMode(params.mode);
        break;
      case 'ui.move_mode':
        d.handleMoveMode(params.move_mode);
        break;
      case 'ui.view_mode':
        d.switchView(params.view_mode);
        break;
      case 'ui.google_location':
        d.mapsLink();
        break;
      case 'ui.set_zoom_1x':
        d.handleResetScale();
        break;
      case 'ui.increase_zoom':
        d.handleScaleUp();
        break;
      case 'ui.decrease_zoom':
        d.handleScaleDown();
        break;
      case 'ui.save':
        d.handleSaveAll();
        break;
      case 'ui.clear_canvas':
        d.cleanCanvas();
        break;
      case 'ui.system_losses':
        if (params?.losses) d.setPVwattsLosses(params.losses);
        break;
      case 'ui.shading':
        {
          const serverSide = Boolean(params?.serverSide);
          const selectedDataset = d.state?.weatherDatasetSelected;
          const fetchedPath = d.state?.weatherDatasetFetchedPath;
          if (!isWeatherDatasetReady(selectedDataset, fetchedPath)) {
            const datasetKey = selectedDataset || 'nsrdb_goes_v4_2024';
            const endpoint = d.context?.newEndpoint?.('api/nsrdb/fetch/');
            if (!endpoint || !d.context?.lead?.uid) {
              d.handleShading(serverSide, true);
              break;
            }
            try {
              await ensureWeatherDatasetReady(d, datasetKey);
              postMessage('State', d.getUIState());
            } catch (err) {
              postMessage('Error', {
                message: err?.message || 'Failed to fetch weather dataset',
              });
              break;
            }
          }
          d.handleShading(serverSide, true);
        }
        break;
      case 'ui.panel_placement':
        await runPanelPlacementCommand(d, params, (err) => {
          console.log('ui.panel_placement applyProductionSettings error:', err);
        });
        break;
      case 'ui.shape':
        d.handleTool(params.shape);
        break;
      case 'ui.set_obstacle_height':
        d.handleObstacleHeight(params.index, params.obstacle_height);
        break;
      case 'ui.set_obstacle_radius':
        d.handleObstacleRadius(params.index, params.obstacle_radius);
        break;
      case 'ui.set_obstacle_setback':
        d.handleObstacleSetback(params.index, params.obstacle_setback);
        break;
      case 'ui.set_obstacle_orientation':
        d.handleObstacleOrientation(params.index, params.orientation);
        break;
      case 'ui.update_tree':
        handleUpdateTree(d, params);
        break;
      case 'ui.set_annotations':
        if (Array.isArray(params?.annotations)) {
          d.setAnnotations(params.annotations);
        }
        break;
      case 'ui.remove_annotation':
        d.removeAnnotation(params.index);
        break;
      case 'ui.set_setback_mode':
        d.handleSetbackMode(params.setback_mode);
        break;
      case 'ui.setback_value':
        d.handleFireSetbacks(params.setback, params.label);
        break;
      case 'ui.set_solar_panel':
        d.changePanelHardware(params.solar_panel);
        break;
      case 'ui.set_buffer':
        d.handleBuffer(params.buffer);
        break;
      case 'ui.set_panel_azimuth':
        d.handlePanelAzimuth(params.index, params.azimuth);
        break;
      case 'ui.set_panel_placement_only_selected_roof':
        if (typeof params?.onlySelectedRoof === 'boolean') {
          d.setPanelPlacementOnlySelectedRoof(params.onlySelectedRoof);
        }
        break;
      case 'ui.set_panel_placement_layout_mode': {
        const layoutMode = params?.layoutMode ?? params?.layout_mode;
        const numeric = Number(layoutMode);
        if (Number.isFinite(numeric)) {
          d.setPanelPlacementLayoutMode(numeric);
        }
        break;
      }
      case 'ui.set_panel_placement_fill_mode': {
        const fillMode = params?.fillMode ?? params?.fill_mode;
        const numeric = Number(fillMode);
        if (Number.isFinite(numeric)) {
          d.setPanelPlacementFillMode(numeric);
        }
        break;
      }
      case 'ui.set_panel_tilt':
        d.handlePanelTilt(params.panelTilt);
        break;
      case 'ui.auto_azimuth':
        d.automaticAzimuthMode(params.autoAzimuth);
        break;
      case 'ui.set_panel_row_spacing':
        d.handleRowSpacing(params.rowSpacing);
        break;
      case 'ui.set_azimuth_restriction':
        if (Array.isArray(params?.azimuthRestriction)) {
          d.handleAzimuthRestriction(params.azimuthRestriction);
        }
        break;
      case 'ui.capture_image':
        d.capture_image().then((data) => {
          if (window.parent) postToParent('ui.capture_image', data);
        });
        break;
      case 'ui.clear_panels':
        d.clearPanels();
        break;
      case 'ui.add_panel':
        d.handleNewPanel();
        break;
      case 'ui.delete_panel':
        d.handleDeletePanel();
        break;
      case 'ui.rotate_panel':
        d.handleRotatePanel();
        break;
      case 'ui.place_ground_mount':
        d.placeGroundMount(
          params.gridOption,
          params.segmentOption,
          params.panelOptions
        );
        break;
      case 'ui.translate_canvas':
        d.translateCanvas(
          params.offsetX,
          params.offsetY,
          params.flipX,
          params.flipY,
          params.translateRoofs,
          params.translateObstacles,
          params.translateTrees
        );
        break;
      case 'ui.set_inverter_quantity':
        handleSetInverterQuantity(d, params);
        break;
      case 'ui.set_weather_dataset':
        if (params?.datasetKey || params?.dataset) {
          d.setWeatherDatasetSelected(params.datasetKey || params.dataset);
        }
        break;
      case 'ui.list_weather_datasets':
        {
          const endpoint = d.context?.newEndpoint?.('api/nsrdb/datasets/');
          if (!endpoint) {
            postMessage('WeatherDatasets', null);
            break;
          }
          endpoint
            .list()
            .then((data) => {
              postMessage('WeatherDatasets', data || null);
            })
            .catch(() => {
              postMessage('WeatherDatasets', null);
            });
        }
        break;
      default:
        break;
    }
  };

  const handleMessage = async (event) => {
    // Integrators may use different origins, but only the window that embeds
    // this iframe is allowed to invoke authenticated designer commands.
    if (!isTrustedParentMessage(event, window.parent)) return;
    const { cmd, params = null, config = null } = event.data;
    const commandTelemetry = cmd
      ? startCommandTelemetry(cmd, {
          hasParams: params !== null && params !== undefined,
          hasConfig: config !== null && config !== undefined,
        })
      : null;

    let amendedConfig = config;

    if (cmd !== undefined) {
      if (cmd.startsWith('proposal.') && cmd === 'proposal.LoadProposal') {
        if (config) {
          amendedConfig = normalizeConfig({
            ...config,
            companyPanelID: null,
            companyInverterID: null,
            companyAdditionalInverterIDs: [],
            companyPowerOptimizerID: null,
          });
        }
      }
    }

    if (cmd !== undefined) {
      if (cmd.startsWith('ui.')) {
        await handleUIMessage(cmd, params, event);
        endCommandTelemetry(commandTelemetry?.commandId);
        return;
      }
    }

    if (config) {
      // iframe config validation
      try {
        if (cmd === 'proposal.LoadProposal') {
          assert(amendedConfig, IFrameConfigScheme);
          const persistentConfig = { ...amendedConfig };
          delete persistentConfig.design_id;
          delete persistentConfig.canvas_id;
          localStorage.setItem(
            'iframe.config',
            JSON.stringify(persistentConfig)
          );
          configStateRef.current = persistentConfig;
          setConfigState(persistentConfig);
        } else {
          const renamed = renameConfigKeys(config);
          const normalized = normalizeConfig(renamed);
          assert(normalized, IFrameConfigScheme);

          if (
            cmd === 'generate' ||
            cmd === 'rerun' ||
            cmd === 'rerun_panel_placement'
          ) {
            if (
              normalized?.companyPanelID === null ||
              normalized?.companyPanelID === undefined
            ) {
              if (normalized?.panelType) {
              } else {
                if (normalized?.panelDefault !== 'default') {
                  return postToParent('Error', {
                    code: '445',
                    message:
                      "Design couldn't start- iframe panel config is not set properly!",
                  });
                }
              }
            }
          }
          // convert buffer value from inch to mm
          normalized.buffer = inchToCm(normalized.buffer) * 10;
          const persistentConfig = { ...normalized };
          delete persistentConfig.design_id;
          delete persistentConfig.canvas_id;
          localStorage.setItem(
            'iframe.config',
            JSON.stringify(persistentConfig)
          );
          configStateRef.current = persistentConfig;
          setConfigState(persistentConfig);
          amendedConfig = normalized;
        }
      } catch (err) {
        if (cmd === 'generate') {
          endGenerateCommandTelemetry('error', { message: err?.message });
        } else {
          endCommandTelemetry(commandTelemetry?.commandId, 'error', {
            message: err?.message,
          });
        }
        return designer.current.displayValidationErrors(err);
      }
    }

    setErrors(null);

    // 'initiated' callbacks are for confirmation of received messaged
    if (cmd !== undefined) {
      const isDesignerProductionCommand = [
        'designer.CalculateProduction',
        'designer.calculateProduction',
      ].includes(cmd);
      if (
        isDesignerProductionCommand &&
        pid !== null &&
        proposal.current
      ) {
        if (window.parent) postToParent(`${cmd}`, 'initiated');
        registerCommandCompletion(
          commandTelemetry?.commandId,
          'Production'
        );
        try {
          await proposal.current.handleMessage(event);
        } catch (err) {
          removeCommandCompletion(
            commandTelemetry?.commandId,
            'Production'
          );
          endCommandTelemetry(commandTelemetry?.commandId, 'error', {
            message:
              err?.message || 'Proposal production calculation failed',
          });
          postToParent('Error', {
            code: '60',
            message:
              err?.message || 'Proposal production calculation failed',
          });
        }
      } else if (cmd.startsWith('designer.') && designer.current) {
        if (window.parent) postToParent(`${cmd}`, 'initiated');
        registerCommandCompletion(
          commandTelemetry?.commandId,
          isDesignerProductionCommand ? 'Production' : cmd
        );
        if (isDesignerProductionCommand) {
          try {
            await designer.current.handleMessage(event);
          } catch (err) {
            removeCommandCompletion(
              commandTelemetry?.commandId,
              'Production'
            );
            endCommandTelemetry(commandTelemetry?.commandId, 'error', {
              message:
                err?.message || 'Solar production calculation failed',
            });
            postToParent('Error', {
              code: '60',
              message:
                err?.message || 'Solar production calculation failed',
            });
          }
        } else {
          designer.current.handleMessage(event);
        }
      } else if (isDesignerProductionCommand) {
        if (window.parent) postToParent(`${cmd}`, 'initiated');
        endCommandTelemetry(commandTelemetry?.commandId, 'error', {
          message: 'Production tool is not ready',
        });
        postToParent('Error', {
          code: '50',
          message: 'Production tool is not ready',
        });
      } else if (cmd.startsWith('proposal.') && proposal.current) {
        if (window.parent) postToParent(`${cmd}`, 'initiated');
        registerCommandCompletion(commandTelemetry?.commandId, cmd);
        proposal.current.handleMessage(event);
      } else if (cmd === 'generate' && designer.current) {
        if (window.parent) postToParent(`${cmd}`, 'initiated');
        try {
          assert(params, IFrameNewLeadParamsScheme);
          if (
            !config &&
            typeof params?.includeHourlyProduction === 'boolean'
          ) {
            setConfigState((prev) => {
              const next = {
                ...(prev || {}),
                includeHourlyProduction: params.includeHourlyProduction,
              };
              localStorage.setItem('iframe.config', JSON.stringify(next));
              configStateRef.current = next;
              return next;
            });
          }
          // designer.current.loadLead(params.uid, true);
          designer.current.newLead(params);
        } catch (err) {
          endGenerateCommandTelemetry('error', { message: err?.message });
          designer.current.displayValidationErrors(err);
        }
      } else if (cmd === 'rerun' && designer.current) {
        if (window.parent) postToParent(`${cmd}`, 'initiated');
        try {
          assert(params, IFrameRerunParamsScheme);
          designer.current.runPrediction(
            mergeCommandOptions(amendedConfig, params)
          );
          endCommandTelemetry(commandTelemetry?.commandId);
        } catch (err) {
          endCommandTelemetry(commandTelemetry?.commandId, 'error', {
            message: err?.message,
          });
          designer.current.displayValidationErrors(err);
        }
      } else if (cmd === 'rerun_panel_placement' && designer.current) {
        // method to rerun design only for changing panel fillMode
        if (window.parent) postToParent(`${cmd}`, 'initiated');
        const commandOptions = mergeCommandOptions(amendedConfig, params);
        designer.current.loadLeadPanelPlacement(
          commandOptions.uid,
          commandOptions
        );

        await new Promise((r) => setTimeout(r, 5000));

        let tryCount = 3;
        while (tryCount > 0) {
          try {
            while (!designer.current?.checkIsLoaded()) {
              console.log('loading');
              await new Promise((r) => setTimeout(r, 1000));
            }
            break;
          } catch (err) {
            console.log(err);
          }
          await new Promise((r) => setTimeout(r, 5000));
          tryCount -= 1;
        }

        designer.current.handleCurrentAccordion('revise');

        await new Promise((r) => setTimeout(r, 1500));

        try {
          await designer.current.handlePlacement();
        } catch (err) {
          console.log(err);
          endCommandTelemetry(commandTelemetry?.commandId, 'error', {
            message: 'Error in panel placement',
          });
          if (window.parent)
            return postToParent('Error', {
              code: '54',
              message: 'Error in panel placement',
            });
        }

        await new Promise((r) => setTimeout(r, 5000));

        try {
          await designer.current.handleProposal(commandOptions, true);
          endCommandTelemetry(commandTelemetry?.commandId);
        } catch (err) {
          console.log(err);
          endCommandTelemetry(commandTelemetry?.commandId, 'error', {
            message: 'Error in creating proposal',
          });
          if (window.parent)
            return postToParent('Error', {
              code: '53',
              message: 'Error in creating proposal',
            });
        }
      } else if (
        cmd === 'run_shading_and_create_proposal' &&
        designer.current
      ) {
        const commandOptions = mergeCommandOptions(amendedConfig, params);
        debugLog('run_shading_and_create_proposal:start', {
          params: commandOptions,
          hasConfig: Boolean(config),
        });
        // method to run design from a pre-created lead that only needs shading to create proposal
        if (window.parent) postToParent(`${cmd}`, 'initiated');

        try {
          await validateLeadExists(commandOptions.uid);
        } catch (err) {
          debugLog('validateLead:error', err);
          endCommandTelemetry(commandTelemetry?.commandId, 'error', {
            message: err?.message || 'Invalid lead uid',
          });
          return postToParent('Error', {
            code: '41',
            message: err?.message || 'Invalid lead uid',
          });
        }

        debugLog('removeShadingGradients:before', {
          uid: commandOptions.uid,
          design_id: getDesignId(commandOptions),
        });
        try {
          await removeShadingGradients(commandOptions.uid, commandOptions);
        } catch (err) {
          debugLog('removeShadingGradients:error', err);
          endCommandTelemetry(commandTelemetry?.commandId, 'error', {
            message: err?.message,
          });
          return postToParent('Error', {
            code: '52',
            message: err?.message || 'Failed to clear previous shading gradients',
          });
        }

        const currentDesignerLead = isCurrentDesignerLead(
          commandOptions.uid,
          commandOptions
        );
        debugLog('loadLeadPanelPlacement:before', {
          uid: commandOptions.uid,
          design_id: getDesignId(commandOptions),
          isCurrentDesignerLead: currentDesignerLead,
          loadedCallbackReceived: designerLoadedRef.current,
        });
        if (!currentDesignerLead) {
          designerLoadedRef.current = false;
        }
        const navigationStarted = designer.current.loadLeadPanelPlacement(
          commandOptions.uid,
          commandOptions
        );
        debugLog('loadLeadPanelPlacement:after-call', {
          navigationStarted,
          loadedCallbackReceived: designerLoadedRef.current,
        });

        await new Promise((r) => setTimeout(r, 5000));
        debugLog('loadLeadPanelPlacement:after-initial-wait');

        debugLog('checkIsLoaded:attempt');
        const designerLoaded = await waitForDesignerLoaded();

        if (!designerLoaded) {
          endCommandTelemetry(commandTelemetry?.commandId, 'error', {
            message: 'Timed out waiting for designer to load',
          });
          return postToParent('Error', {
            code: '50',
            message: 'Timed out waiting for designer to load',
          });
        }

        let designTool;
        try {
          debugLog('waitForDesignerTool:before');
          designTool = await waitForDesignerTool([
            'applyProductionSettings',
            'handleCurrentAccordion',
            'handleShading',
          ]);
          debugLog('waitForDesignerTool:after');
        } catch (err) {
          debugLog('waitForDesignerTool:error', err);
          endCommandTelemetry(commandTelemetry?.commandId, 'error', {
            message: 'Designer tool methods are not ready',
          });
          return postToParent('Error', {
            code: '50',
            message: 'Designer tool methods are not ready',
          });
        }

        const configForRun =
          amendedConfig && typeof amendedConfig === 'object'
            ? amendedConfig
            : configState;
        debugLog('configForRun:resolved', configForRun);

        try {
          if (configForRun && typeof configForRun === 'object') {
            if (designTool.applyProductionSettings) {
              debugLog('applyProductionSettings:before');
              await designTool.applyProductionSettings({ ...configForRun });
              debugLog('applyProductionSettings:after');
            }
          }
        } catch (err) {
          debugLog('applyProductionSettings:error', err);
          endCommandTelemetry(commandTelemetry?.commandId, 'error', {
            message: err?.message || 'Invalid production configuration',
          });
          return postToParent('Error', {
            code: '51',
            message: err?.message || 'Invalid production configuration',
          });
        }

        debugLog('handleCurrentAccordion:segments:before');
        const emptySegments =
          designTool.handleCurrentAccordion('segments');
        debugLog('handleCurrentAccordion:segments:after', {
          result: emptySegments,
        });

        await new Promise((r) => setTimeout(r, 1000));
        debugLog('segments:post-wait', {
          emptySegments,
          panelCount: getDesignerPanelCount(),
        });

        if (!emptySegments) {
          debugLog('segments:empty-error');
          if (window.parent) {
            return postToParent('Error', {
              code: '42',
              message: '0 number of predicted roof segments',
            });
          }
          return;
        }

        debugLog('panelCount:before-saved-panel-check', {
          panelCount: getDesignerPanelCount(),
        });
        if (getDesignerPanelCount() === 0) {
          try {
            debugLog('waitForSavedPanelsToLoad:before');
            await waitForPanelsToLoad(10000);
            debugLog('waitForPanelsToLoad:after', {
              panelCount: getDesignerPanelCount(),
            });
          } catch (err) {
            debugLog('savedPanelPlacement:missing', err);
            endCommandTelemetry(commandTelemetry?.commandId, 'error', {
              message: 'No saved panel layout was loaded for this lead',
            });
            return postToParent('Error', {
              code: '54',
              message:
                'No saved panel layout was loaded for this lead. Create or load a panel layout before run_shading_and_create_proposal.',
            });
          }
        }

        debugLog('panelCount:after-saved-panel-check', {
          panelCount: getDesignerPanelCount(),
        });
        if (getDesignerPanelCount() === 0) {
          debugLog('panelCount:no-panels-error');
          endCommandTelemetry(commandTelemetry?.commandId, 'error', {
            message: 'No panel layout is available to create proposal',
          });
          return postToParent('Error', {
            code: '54',
            message: 'No panel layout is available to create proposal',
          });
        }

        debugLog('handleCurrentAccordion:shading:before');
        designTool.handleCurrentAccordion('shading');
        debugLog('handleCurrentAccordion:shading:after');

        try {
          debugLog('handleShading:before');
          const shadingResult = await designTool.handleShading();
          debugLog('handleShading:after', {
            result: shadingResult,
          });
          if (shadingResult === false) {
            throw new Error('Error in shading simulation');
          }
        } catch (err) {
          debugLog('handleShading:error', err);
          endCommandTelemetry(commandTelemetry?.commandId, 'error', {
            message: 'Error in shading simulation',
          });
          return postToParent('Error', {
            code: '52',
            message: 'Error in shading simulation',
          });
        }

        const proposalParams = mergeCommandOptions(configForRun, params);
        debugLog('checkShadingGradients:start-polling', {
          proposalParams,
        });
        // Use the same canvas target for navigation, shading deletion,
        // gradient polling, production, and proposal creation.
        startGradientPolling(
          commandOptions.uid,
          proposalParams,
          true,
          commandTelemetry?.commandId
        );

        // designer.current.handleShading().then(res => {
        //   const interval = setInterval(() => checkShadingGradients(params?.uid, params), 2500);
        //   setCheckGradientsInterval(interval);
        // }).catch((err) => {
        //   console.log(`iframe shading error: ${err}`);
        //   return window.parent.postMessage({ callbackType: `Error`, data: { code: '52', message: 'Error in shading simulation' } }, '*');
        // });

        // await new Promise((r) => setTimeout(r, 10000));

        // try {
        //   await designer.current.handleProposal(params, true);
        // } catch (err) {
        //   return window.parent.postMessage({ callbackType: `Error`, data: { code: '53', message: 'Error in creating proposal' } }, '*');
        // }

        // designer.current.handleProposal(params, true);
      } else if (cmd === RUN_PRODUCTION_AND_CREATE_PROPOSAL) {
        const commandOptions = mergeCommandOptions(amendedConfig, params);
        debugLog('run_production_and_create_proposal:start', {
          params: commandOptions,
          hasConfig: Boolean(config),
        });
        if (window.parent) postToParent(`${cmd}`, 'initiated');

        if (!designer.current) {
          endCommandTelemetry(commandTelemetry?.commandId, 'error', {
            message: 'Designer is not ready',
          });
          return postToParent('Error', {
            code: '50',
            message: 'Designer is not ready',
          });
        }

        stopGradientPolling();

        try {
          await validateLeadExists(commandOptions.uid);
        } catch (err) {
          debugLog('validateLead:error', err);
          endCommandTelemetry(commandTelemetry?.commandId, 'error', {
            message: err?.message || 'Invalid lead uid',
          });
          return postToParent('Error', {
            code: '41',
            message: err?.message || 'Invalid lead uid',
          });
        }

        const currentDesignerLead = isCurrentDesignerLead(
          commandOptions.uid,
          commandOptions
        );
        debugLog('loadLeadPanelPlacement:before', {
          uid: commandOptions.uid,
          design_id: getDesignId(commandOptions),
          isCurrentDesignerLead: currentDesignerLead,
          loadedCallbackReceived: designerLoadedRef.current,
        });
        if (!currentDesignerLead) {
          designerLoadedRef.current = false;
        }
        const navigationStarted = designer.current.loadLeadPanelPlacement(
          commandOptions.uid,
          commandOptions
        );
        debugLog('loadLeadPanelPlacement:after-call', {
          navigationStarted,
          loadedCallbackReceived: designerLoadedRef.current,
        });

        await new Promise((r) => setTimeout(r, 5000));
        debugLog('loadLeadPanelPlacement:after-initial-wait');

        debugLog('checkIsLoaded:attempt');
        const designerLoaded = await waitForDesignerLoaded();

        if (!designerLoaded) {
          endCommandTelemetry(commandTelemetry?.commandId, 'error', {
            message: 'Timed out waiting for designer to load',
          });
          return postToParent('Error', {
            code: '50',
            message: 'Timed out waiting for designer to load',
          });
        }

        let designTool;
        try {
          debugLog('waitForDesignerTool:before');
          designTool = await waitForDesignerTool([
            'applyProductionSettings',
            'handleCurrentAccordion',
            'handleProposal',
          ]);
          debugLog('waitForDesignerTool:after');
        } catch (err) {
          debugLog('waitForDesignerTool:error', err);
          endCommandTelemetry(commandTelemetry?.commandId, 'error', {
            message: 'Designer tool methods are not ready',
          });
          return postToParent('Error', {
            code: '50',
            message: 'Designer tool methods are not ready',
          });
        }

        const configForRun =
          amendedConfig && typeof amendedConfig === 'object'
            ? amendedConfig
            : configState;
        const proposalParams = mergeCommandOptions(configForRun, params);
        debugLog('configForRun:resolved', configForRun);
        registerCommandCompletion(commandTelemetry?.commandId, 'NewProposal');

        try {
          await runProductionAndCreateProposal({
            designTool,
            configForRun,
            getPanelCount: getDesignerPanelCount,
            waitForPanels: () => waitForPanelsToLoad(10000),
            runProduction: (settings) => runProductionFromChain(settings),
            proposalParams,
            loadAfter: true,
            afterSegmentsWait: () => new Promise((r) => setTimeout(r, 1000)),
          });
          endCommandTelemetry(commandTelemetry?.commandId);
        } catch (err) {
          debugLog('run_production_and_create_proposal:error', err);
          endCommandTelemetry(commandTelemetry?.commandId, 'error', {
            message: err?.message,
          });
          return postToParent('Error', {
            code: err?.code || '53',
            message: err?.message || 'Error in creating proposal',
          });
        }
      } else if (cmd === 'coverage') {
        if (window.parent) postToParent(`${cmd}`, 'initiated');

        try {
          assert(params, IFrameCoverageAPIScheme);
        } catch (err) {
          commandTelemetryRef.current.pendingByCallback.delete(cmd);
          endCommandTelemetry(commandTelemetry?.commandId, 'error', {
            message: err?.message || 'Invalid coverage params',
          });
          if (designer.current?.displayValidationErrors) {
            designer.current.displayValidationErrors(err);
          } else if (window.parent) {
            postToParent('coverage', false);
          }
          return;
        }

        const designerReady = await waitForDesignerReady();
        if (!designerReady) {
          commandTelemetryRef.current.pendingByCallback.delete(cmd);
          endCommandTelemetry(commandTelemetry?.commandId, 'error', {
            message: 'Designer not ready for coverage check',
          });
          if (window.parent) {
            postToParent('coverage', false);
          }
          return;
        }

        registerCommandCompletion(commandTelemetry?.commandId, cmd);
        try {
          await designer.current.checkCoverage(params);
        } catch (err) {
          commandTelemetryRef.current.pendingByCallback.delete(cmd);
          endCommandTelemetry(commandTelemetry?.commandId, 'error', {
            message: err?.message || 'Error in coverage check',
          });
          if (window.parent) {
            postToParent('coverage', false);
          }
        }
      } else if (cmd === 'Ping') {
        if (window.parent) {
          postToParent('Ping', 'Pong');
        }
        endCommandTelemetry(commandTelemetry?.commandId);
      }
    }
  };

  handleMessageRef.current = handleMessage;

  useEffect(() => {
    const latestMessageListener = createLatestMessageListener(handleMessageRef);
    window.addEventListener('message', latestMessageListener);
    return () => {
      window.removeEventListener('message', latestMessageListener);
    };
  }, []);

  // returns only if loaded in iframe
  if (window.location !== window.parent.location) {
    return (
      <AuthProvider iframe apiKey={apiKey}>
        <LeadProvider>
          <LeadContext.Consumer>
            {({ lead, user }) => {
              const loaded = Boolean(lead);
              const hasError = Boolean(errors);
              const companyName = getIframeCompanyName(user);
              if (user) {
                return (
                  <>
                    <IFrameError visible={hasError} errors={errors} />
                    <Home
                      visible={!loaded && !hasError}
                      companyName={companyName}
                    />
                    <Proposal
                      visible={loaded && !hasError && pid !== null}
                      context={configState}
                      callback={callback}
                      companyName={companyName}
                      pid={pid}
                      ref={proposal}
                    />
                    <Designer
                      visible={loaded && !hasError && pid === null}
                      context={configState}
                      callback={callback}
                      companyName={companyName}
                      designFrame={designFrame}
                      ref={designer}
                    />
                  </>
                );
              }
            }}
          </LeadContext.Consumer>
        </LeadProvider>
      </AuthProvider>
    );
  }
};

export default IFrame;
